import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { GIFT_COPY } from '@garlic/shared';
import { api, ApiError, apiUrl } from '../../lib/api';
import { formatBRL, formatDateTime } from '../../lib/format';
import { useGuestRealtime } from '../../lib/realtime';
import { Alert, Spinner } from '../../ui/primitives';
import { RestaurantAvatar } from '../RestaurantBadge';
import type { TenantRestaurant } from '../TenantApp';
import { publicPath } from '../../lib/paths';
import { GiftCard } from './GiftCard';

type ReceiptResponse = {
  receipt: {
    id: string; code: string; qrPayload: string; tableLabel: string; sessionNumber: number; consumedCents: number; discountCents: number; paidCents: number; dueCents: number;
    settledAt: string; expiresAt: string; used: boolean; currentlySettled: boolean; currentDueCents: number;
  };
  gift: { enabled: boolean; reviewUrl: string | null; clicked: boolean };
};

/**
 * Comprovante de saída. O QR contém só o código; quem decide se a saída está liberada é o servidor,
 * consultado pelo balconista. O bloco do brinde convida a avaliar no Google (texto em GIFT_COPY); o sistema
 * só registra o clique, não verifica avaliação.
 */
export function ExitReceipt({ restaurant }: { restaurant: TenantRestaurant }) {
  const q = useQuery({ queryKey: ['guest-receipt'], queryFn: () => api<ReceiptResponse>('/api/public/receipt'), retry: false });
  useGuestRealtime(q.isSuccess);
  const [qr, setQr] = useState<string>();

  useEffect(() => {
    if (q.data) void QRCode.toDataURL(q.data.receipt.qrPayload, { margin: 1, width: 360, errorCorrectionLevel: 'M' }).then(setQr);
  }, [q.data]);

  if (q.isPending) return <Spinner label="Gerando seu comprovante…" />;
  if (q.isError) {
    const notSettled = q.error instanceof ApiError && q.error.status === 409;
    return (
      <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-4 text-center">
        <div>
          <p className="text-lg font-semibold">{notSettled ? 'A conta ainda não está quitada' : 'Comprovante indisponível'}</p>
          <p className="mt-1 text-sm text-ink-3">{q.error instanceof ApiError ? q.error.message : 'Tente novamente.'}</p>
          <Link to={publicPath('/conta')} className="mt-4 inline-block text-sm font-medium text-[var(--brand)] underline">Ver a conta</Link>
        </div>
      </main>
    );
  }
  const { receipt: r, gift } = q.data;

  const recordClick = () => {
    // O link abre na hora (sem esperar a rede, para não ser bloqueado); o clique é registrado em paralelo.
    void fetch(apiUrl('/api/public/receipt/review-click'), { method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => {});
  };

  return (
    <div className="mx-auto min-h-dvh max-w-md px-4 pb-12 pt-4">
      <Link to={publicPath('/mesa')} className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink"><ArrowLeft className="size-4" /> Minha mesa</Link>

      <article className="mt-4 overflow-hidden rounded-3xl border border-line bg-white shadow-card">
        <header className="flex flex-col items-center gap-2 bg-[var(--brand)] px-6 pb-6 pt-7 text-center text-[var(--brand-contrast)]">
          <RestaurantAvatar r={restaurant} className="size-16 ring-4 ring-white/30" />
          <p className="font-display text-xl font-semibold">{restaurant.name}</p>
        </header>

        {!r.currentlySettled ? (
          <div className="p-6"><Alert tone="danger" title="Conta com pendência">Apareceu um valor em aberto de {formatBRL(r.currentDueCents)} nesta conta. Fale com o caixa antes de sair.</Alert></div>
        ) : (
          <div className="flex flex-col items-center px-6 pt-6 text-center">
            <p className="inline-flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-sprout-600"><CheckCircle2 className="size-8" /> CONTA PAGA</p>
            {r.used && <p className="mt-1 text-sm font-medium text-ink-3">Saída já registrada pelo balconista.</p>}
          </div>
        )}

        <dl className="mx-6 mt-5 divide-y divide-dashed divide-line border-y border-dashed border-line text-sm">
          <Row label="Mesa" value={r.tableLabel} />
          <Row label="Comanda" value={`nº ${r.sessionNumber}`} />
          <Row label="Total consumido" value={formatBRL(r.consumedCents)} />
          {r.discountCents > 0 && <Row label="Descontos" value={`− ${formatBRL(r.discountCents)}`} />}
          <Row label="Total pago" value={formatBRL(r.paidCents)} />
          <Row label="Saldo pendente" value={formatBRL(r.currentlySettled ? 0 : r.currentDueCents)} strong />
          <Row label="Quitada em" value={formatDateTime(r.settledAt, restaurant.timezone)} />
        </dl>

        <div className="flex flex-col items-center px-6 py-6 text-center">
          {qr && <img src={qr} alt="QR Code de validação da saída" className="size-52 rounded-2xl border border-line p-2" />}
          <p className="mt-3 text-xs uppercase tracking-wider text-ink-3">Código de validação</p>
          <p className="font-mono text-2xl font-semibold tracking-[0.2em]">{r.code}</p>
          <p className="mt-3 text-sm text-ink-2">Apresente este comprovante ao balconista na saída.</p>
          <p className="mt-1 text-xs text-ink-3">Válido até {formatDateTime(r.expiresAt, restaurant.timezone)}.</p>
        </div>
      </article>

      {gift.enabled && (
        <GiftCard className="mt-6" reviewUrl={gift.reviewUrl} thanksText={GIFT_COPY.thanks} initiallyClicked={gift.clicked} onReviewClick={recordClick} />
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3 py-2">
      <dt className="text-ink-3">{label}</dt>
      <dd className={strong ? 'font-semibold' : ''}>{value}</dd>
    </div>
  );
}
